import type { Address } from "viem"
import { action, computed, makeObservable, observable } from "mobx"

import type { GetTokenByFilter } from "@/domains/tokens/types"
import { ChainConfigToken, LcAddress } from "@/domains/core"
import { WETH } from "@/domains/tokens/static-tokens"
import { TokenStore } from "@/domains/tokens/token-store"

export type SystemTokenConfig = {
  native: ChainConfigToken
  wrappedNative: Address
  governance: Address
}

export class TokenRepository {
  private tokens = new Map<string, TokenStore>()

  constructor(private readonly systemTokenConfig: SystemTokenConfig) {
    makeObservable(this, {
      tokens: observable,
      add: action,
      totalTokens: computed,
      totalValueUSD: computed,
    })
  }

  // Simple find method - returns actual TokenStore instance
  find(address: LcAddress): TokenStore | undefined {
    return this.tokens.get(address.toString())
  }

  // Add token to repository
  add(token: TokenStore): void {
    const existing = this.find(token.address)
    if (existing) {
      return
    }

    this.tokens.set(token.address.toString(), token)
  }

  // Get all tokens
  getAll(): TokenStore[] {
    return Array.from(this.tokens.values())
  }

  getByFilter(filter: GetTokenByFilter): TokenStore[] {
    return this.getAll().filter((token) => {
      if (filter.isSwapToken !== undefined) {
        return filter.isSwapToken == token.isSwapToken
      }
      if (filter.isIncentiveToken !== undefined) {
        return filter.isIncentiveToken == token.isIncentiveToken
      }
      if (filter.isWhitelisted !== undefined) {
        return filter.isWhitelisted == token.isWhitelisted
      }
      if (filter.isSystemToken !== undefined) {
        if (filter.isSystemToken) {
          return token.source === "system"
        } else {
          return token.source !== "system"
        }
      }
      if (filter.isUserToken !== undefined) {
        if (filter.isUserToken) {
          return token.source === "user"
        } else {
          return token.source !== "user"
        }
      }
      return true
    })
  }

  getSystemTokenConfig(): SystemTokenConfig {
    return this.systemTokenConfig
  }

  /**
   * Sync native token price from API.
   * Updates WETH; ETH delegates to WETH for bestPrice so it reflects this automatically.
   */
  syncNativePriceFromApi(price: number) {
    WETH.updateApiPrice(price)
  }

  // Get system tokens only
  getSystemTokens(): TokenStore[] {
    return this.getAll().filter((token) => token.source === "system")
  }

  getWhitelistedSystemTokens(): TokenStore[] {
    return this.getAll().filter((token) => token.source === "system" && token.isWhitelisted)
  }

  // Get user tokens only
  getUserTokens(): TokenStore[] {
    return this.getAll().filter((token) => token.source === "user")
  }

  /**
   * Clear all token balances to zero
   * Useful when disconnecting wallet or resetting state
   */
  public clearAllBalances(): void {
    this.tokens.forEach((token) => {
      token.updateBalance(0n)
    })
  }

  // Compatibility methods for existing services
  getToken(address: LcAddress): TokenStore | null {
    return this.find(address) || null
  }

  getAllTokensArray(): TokenStore[] {
    return this.getAll()
  }

  get totalTokens(): number {
    return this.tokens.size
  }

  get totalValueUSD(): number {
    return Array.from(this.tokens.values()).reduce((total, token) => {
      const balance = token.displayBalance
      return total + Number(balance) * token.bestPrice
    }, 0)
  }
}
